/**
 * @group Server
 * 
 * @summary
 * <blockquote>Determines if server RequestContextManager should be loaded based on runtime environment</blockquote>
 * 
 * @remarks
 * Checks Next.js runtime environment variables to determine if the server RequestContextManager
 * (with AsyncLocalStorage) should be loaded. Prevents loading in Edge runtime to avoid Turbopack
 * resolution issues, but allows loading in Node.js runtime or when NEXT_RUNTIME is not set (e.g., tests).
 * 
 * Uses the centralized `getNextjsRuntime()` utility for runtime detection.
 * 
 * @returns `true` if server RequestContextManager should be loaded, `false` otherwise
 * 
 * @example
 * ```typescript
 * import { shouldLoadServerRequestContext } from '@shared/config/src/server/shouldLoadServerRequestContext';
 * 
 * if (shouldLoadServerRequestContext()) {
 *   // Load server RequestContextManager
 * }
 * ```
 */

import { getNextjsRuntime } from '@shared/utils';

/**
 * Determines if server RequestContextManager should be loaded based on runtime environment
 * 
 * @returns `true` if server RequestContextManager should be loaded, `false` otherwise
 */
/**
 * @internal
 */
export function shouldLoadServerRequestContext(): boolean {
  const runtimeResult = getNextjsRuntime();
  
  return runtimeResult.match({
    ok: (nextRuntime) => {
      const isEdgeRuntime = nextRuntime === 'edge';
      const isNodejsRuntime = nextRuntime === 'nodejs';
      const nextRuntimeNotSet = nextRuntime === undefined;
      
      // Allow loading if:
      // 1. We're explicitly in Node.js runtime (NEXT_RUNTIME === 'nodejs'), OR
      // 2. NEXT_RUNTIME is not set (e.g., in tests) and we're not in Edge runtime
      return !isEdgeRuntime && (isNodejsRuntime || nextRuntimeNotSet);
    },
    err: () => {
      // If runtime detection fails, allow loading (safer default for Node.js environments)
      return true;
    }
  });
}

